import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Home, ChevronRight, ChevronLeft, TrendingUp, Calendar, Eye, ArrowUpRight } from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";

interface NewsItem {
  id: number;
  information_title: string;
  information_content: string;
  tag: string;
  is_top: number;
  release_time: string;
  create_time: string;
}

export default function CompanyCultureListPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const pageSize = 6;

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const resp = await fetch('/api/public/information/company-culture');
      const data = await resp.json();
      if (data.ok) {
        // API已经按 is_top DESC, release_time DESC 排序
        setNews(data.data || []);
      }
    } catch (err) {
      console.error('获取企业新闻列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 从富文本中提取纯文本摘要
  const extractSummary = (html: string, maxLength: number = 150) => {
    if (!html) return '';
    const text = html.replace(/<[^>]+>/g, '');
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  // 格式化日期时间
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).replace(/\//g, '-');
  };

  // 格式化日期（短格式）
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
  };

  // 获取置顶新闻（第一条置顶文章，如果有多条置顶则选择最新的一条）
  const featuredNews = news.length > 0 && news[0].is_top === 1 ? news[0] : null;
  
  const totalPages = Math.ceil(news.length / pageSize);
  const paginatedData = news.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );
  
  // 在第一页时，排除置顶新闻
  const regularNews = currentPage === 1 && featuredNews 
    ? paginatedData.filter(item => item.id !== featuredNews.id)
    : paginatedData;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #3d7ab5 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰线条 */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <TrendingUp style={{ width: isMobile ? '28px' : '40px', height: isMobile ? '28px' : '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              企业新闻
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)' }}>
            新闻实时更新
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#1e3a5f', fontWeight: 500 }}>企业新闻</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            加载中...
          </div>
        ) : news.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
            暂无企业新闻文章
          </div>
        ) : (
          <>
            {/* 置顶新闻 - 只在第一页显示 */}
            {currentPage === 1 && featuredNews && (
              <div
                onClick={() => navigate(`/company-culture/${featuredNews.id}`)}
                onMouseEnter={() => setHoveredItem(-1)}
                onMouseLeave={() => setHoveredItem(null)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: isMobile ? '12px' : '16px',
                  overflow: 'hidden',
                  marginBottom: isMobile ? '20px' : '32px',
                  cursor: 'pointer',
                  boxShadow: hoveredItem === -1 
                    ? '0 20px 60px rgba(30,58,95,0.2)' 
                    : '0 4px 20px rgba(0,0,0,0.08)',
                  transform: hoveredItem === -1 ? 'translateY(-4px)' : 'translateY(0)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                  {/* 左侧图片区域 */}
                  <div style={{
                    width: isMobile ? '100%' : '45%',
                    minHeight: isMobile ? '200px' : '280px',
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.1) 0%, transparent 60%)'
                    }} />
                    <TrendingUp style={{ width: isMobile ? '60px' : '80px', height: isMobile ? '60px' : '80px', color: 'rgba(255,255,255,0.3)' }} />
                    <span style={{
                      position: 'absolute',
                      top: isMobile ? '12px' : '20px',
                      left: isMobile ? '12px' : '20px',
                      padding: isMobile ? '4px 10px' : '6px 14px',
                      backgroundColor: '#fbbf24',
                      color: '#1f2937',
                      fontSize: isMobile ? '11px' : '13px',
                      fontWeight: 600,
                      borderRadius: '20px'
                    }}>
                      热门推荐
                    </span>
                  </div>
                  
                  {/* 右侧内容 */}
                  <div style={{ flex: 1, padding: isMobile ? '20px' : '32px 40px' }}>
                    {featuredNews.tag && (
                      <span style={{
                        display: 'inline-block',
                        padding: isMobile ? '3px 10px' : '4px 12px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 600,
                        borderRadius: '20px',
                        marginBottom: isMobile ? '12px' : '16px'
                      }}>
                        {featuredNews.tag}
                      </span>
                    )}
                    
                    <h2 style={{
                      fontSize: isMobile ? '18px' : '24px',
                      fontWeight: 700,
                      color: hoveredItem === -1 ? '#1e3a5f' : '#1f2937',
                      marginBottom: isMobile ? '12px' : '16px',
                      lineHeight: 1.4,
                      transition: 'color 0.2s'
                    }}>
                      {featuredNews.information_title}
                    </h2>
                    
                    <p style={{
                      fontSize: isMobile ? '13px' : '15px',
                      color: '#6b7280',
                      lineHeight: 1.8,
                      marginBottom: isMobile ? '16px' : '24px'
                    }}>
                      {extractSummary(featuredNews.information_content, 200)}
                    </p>

                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? '12px' : '0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '20px', fontSize: isMobile ? '12px' : '14px', color: '#9ca3af' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                          {formatDateTime(featuredNews.release_time)}
                        </span>
                      </div>
                      
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#1e3a5f',
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: 500
                      }}>
                        阅读全文
                        <ArrowUpRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 新闻列表 */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(2, 1fr)', 
              gap: isMobile ? '16px' : '24px' 
            }}>
              {regularNews.map((item) => (
                <div
                  key={item.id}
                  onClick={() => navigate(`/company-culture/${item.id}`)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: isMobile ? '20px' : '28px',
                    cursor: 'pointer',
                    boxShadow: hoveredItem === item.id 
                      ? '0 12px 40px rgba(30,58,95,0.15)' 
                      : '0 2px 8px rgba(0,0,0,0.06)',
                    transform: hoveredItem === item.id ? 'translateY(-4px)' : 'translateY(0)',
                    transition: 'all 0.3s ease',
                    borderTop: hoveredItem === item.id ? '4px solid #1e3a5f' : '4px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '12px' : '16px', flexWrap: 'wrap' }}>
                    {item.tag && (
                      <span style={{
                        padding: isMobile ? '3px 10px' : '4px 12px',
                        backgroundColor: hoveredItem === item.id ? '#1e3a5f' : '#f0f9ff',
                        color: hoveredItem === item.id ? 'white' : '#0369a1',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 600,
                        borderRadius: '20px',
                        transition: 'all 0.2s'
                      }}>
                        {item.tag}
                      </span>
                    )}
                    {item.is_top === 1 && (
                      <span style={{
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        fontSize: isMobile ? '10px' : '11px',
                        fontWeight: 600,
                        borderRadius: '4px'
                      }}>
                        置顶
                      </span>
                    )}
                  </div>
                  
                  <h3 style={{
                    fontSize: isMobile ? '16px' : '17px',
                    fontWeight: 600,
                    color: hoveredItem === item.id ? '#1e3a5f' : '#1f2937',
                    marginBottom: isMobile ? '10px' : '12px',
                    lineHeight: 1.5,
                    transition: 'color 0.2s',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.information_title}
                  </h3>
                  
                  <p style={{
                    fontSize: isMobile ? '13px' : '14px',
                    color: '#6b7280',
                    lineHeight: 1.7,
                    marginBottom: isMobile ? '16px' : '20px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {extractSummary(item.information_content)}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: isMobile ? '12px' : '13px', color: '#9ca3af' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px' }} />
                        {formatDate(item.release_time)}
                      </span>
                    </div>
                    <ArrowUpRight style={{ 
                      width: isMobile ? '16px' : '18px', 
                      height: isMobile ? '16px' : '18px', 
                      color: hoveredItem === item.id ? '#1e3a5f' : '#d1d5db',
                      transition: 'color 0.2s'
                    }} />
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '6px' : '8px',
                marginTop: isMobile ? '32px' : '48px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === 1 ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <ChevronLeft style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                  上一页
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      width: isMobile ? '32px' : '40px',
                      height: isMobile ? '32px' : '40px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: page === currentPage ? '#1e3a5f' : 'white',
                      color: page === currentPage ? 'white' : '#374151',
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: page === currentPage ? 600 : 400,
                      cursor: 'pointer',
                      boxShadow: page === currentPage ? '0 4px 12px rgba(30,58,95,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '8px 16px' : '10px 20px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === totalPages ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  下一页
                  <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

